// AI credit consumption / breakdown / top-up history models for the SaaS billing prototype.
// See plans/saas-billing/08-ai-credit-usage-display.md.
//
// These types power the /billing/credits detail page. ConsumptionEvent represents a
// single committed AI request, FeatureUsageBreakdown aggregates events by feature
// bucket for the current cycle, and TopUpHistoryEntry lists past one-time top-up
// purchases.
//
// FeatureKey is a finite union of "feature buckets" — generic AI verbs in the
// prototype. The real implementation will map these to actual product features
// once the ledger column is added (see deferred backend checklist in #08).

import { CurrencyCode } from './subscription.model';

export type FeatureKey =
  | 'ai_chat'
  | 'document_extraction'
  | 'image_generation'
  | 'voice_transcription'
  | 'summarization'
  | 'other';

export type ModelTier = 'basic' | 'premium';

/**
 * A single committed AI request. Maps to a `Commit` row in
 * `dbo.TenantAICreditLedger` in the real implementation. The prototype
 * fabricates a deterministic stream of these per fixture.
 */
export interface ConsumptionEvent {
  id: string;
  /** ISO 8601 timestamp. */
  occurredAt: string;
  featureKey: FeatureKey;
  /** Always positive in the prototype (negative would be a refund/adjustment). */
  creditsConsumed: number;
  modelTier: ModelTier;
  /** Short human-readable summary for the activity row, e.g., "Chat: 'invoice question'". */
  requestSummary: string;
  /**
   * Combined remaining balance after this event committed.
   * Matches AICreditBalance.remainingCredits formula from #01:
   *   allocated - used - reserved + topUpBalance
   */
  balanceAfter: number;
}

/**
 * Per-feature aggregation of consumption events for the current cycle.
 * Sum of `percentOfTotal` is ~100 (rounding tolerated).
 */
export interface FeatureUsageBreakdown {
  featureKey: FeatureKey;
  displayName: string;
  creditsUsed: number;
  requestCount: number;
  /** 0-100. */
  percentOfTotal: number;
}

/**
 * One-time top-up pack purchase. Listed in the Top-up history section on
 * /billing/credits. `remaining` lets the UI show how many credits from this
 * specific purchase are still unconsumed; aggregate top-up balance lives on
 * AICreditBalance.topUpBalance.
 */
export interface TopUpHistoryEntry {
  id: string;
  creditTopUpId: string;
  /** ISO 8601 purchase timestamp. */
  purchasedAt: string;
  packDisplayName: string;
  credits: number;
  amountMinor: number;
  currency: CurrencyCode;
  /** Remaining credits from this purchase (can be less than `credits`). */
  remaining: number;
  status: 'initiated' | 'completed' | 'failed' | 'expired';
}
